import * as THREE from 'three';
import type { Tickable } from '../engine/Game';

const SIZE = 200;
const SEGS = 128;

// Cheap layered sine + hashed noise for deterministic terrain.
function hash2(x: number, y: number): number {
  const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return s - Math.floor(s);
}
function valueNoise(x: number, y: number): number {
  const xi = Math.floor(x), yi = Math.floor(y);
  const xf = x - xi, yf = y - yi;
  const a = hash2(xi, yi);
  const b = hash2(xi + 1, yi);
  const c = hash2(xi, yi + 1);
  const d = hash2(xi + 1, yi + 1);
  const sx = xf * xf * (3 - 2 * xf);
  const sy = yf * yf * (3 - 2 * yf);
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy;
}

export class Terrain implements Tickable {
  readonly mesh: THREE.Mesh;
  private pondCenter = new THREE.Vector2(30, -25);
  private pondRadius = 10;

  constructor() {
    const geo = new THREE.PlaneGeometry(SIZE, SIZE, SEGS, SEGS);
    geo.rotateX(-Math.PI / 2);

    const pos = geo.attributes.position as THREE.BufferAttribute;
    const colors = new Float32Array(pos.count * 3);
    const grass = new THREE.Color('#8fd79f');
    const grassDeep = new THREE.Color('#5fb885');
    const sand = new THREE.Color('#f3e1b2');
    const ridge = new THREE.Color('#e6f4d8');

    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      const h = this.heightAt(x, z);
      pos.setY(i, h);

      const distPond = Math.hypot(x - this.pondCenter.x, z - this.pondCenter.y);
      const nearSand = Math.max(0, 1 - Math.max(0, distPond - this.pondRadius) / 3);

      const c = grass.clone();
      if (h > 1.0) c.lerp(ridge, Math.min(1, (h - 1.0) * 0.7));
      else c.lerp(grassDeep, Math.min(1, Math.max(0, -h) * 0.6));
      if (nearSand > 0) c.lerp(sand, nearSand);

      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geo.computeVertexNormals();

    const mat = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 1,
      metalness: 0,
    });
    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.receiveShadow = true;
  }

  heightAt(x: number, z: number): number {
    // Base rolling hills.
    const big = Math.sin(x * 0.04) * Math.cos(z * 0.05) * 1.4;
    const mid = Math.sin(x * 0.12 + 1.3) * Math.cos(z * 0.1 - 0.7) * 0.5;
    const n = valueNoise(x * 0.05, z * 0.05) * 0.8;

    let h = big + mid + n - 0.2;

    // Flatten pond bowl.
    const distPond = Math.hypot(x - this.pondCenter.x, z - this.pondCenter.y);
    if (distPond < this.pondRadius) {
      const t = distPond / this.pondRadius;
      h = THREE.MathUtils.lerp(-1.0, h, t * t);
    }

    // Flatten player house plot (south).
    const house = this.getHousePlot();
    if (Math.abs(x - house.x) < 7 && Math.abs(z - house.z) < 7) {
      h = THREE.MathUtils.lerp(house.y, h, 0);
    }

    // Flatten landmark plots.
    for (const p of this.flatPads) {
      if (Math.abs(x - p.x) < p.r && Math.abs(z - p.z) < p.r) {
        h = p.y;
      }
    }
    return h;
  }

  private flatPads = [
    { x: 40, z: 10, y: 0.3, r: 6 },   // Library
    { x: -10, z: 35, y: 0.3, r: 6 },  // Cafe
    { x: -40, z: -5, y: 0.3, r: 5 },  // Shop
  ];

  getHousePlot(): { x: number; z: number; y: number } {
    return { x: 0, z: -25, y: 0.2 };
  }

  getLandmarkPositions() {
    return {
      library: { x: 40, z: 10, y: 0.3 },
      cafe: { x: -10, z: 35, y: 0.3 },
      shop: { x: -40, z: -5, y: 0.3 },
      pond: { x: this.pondCenter.x, z: this.pondCenter.y },
    };
  }

  tick(): void {}
}
